/**
 * Works the same as $resource, and accepts the same parameters,
 * but adds a caching proxy to *all* GET actions.
 *
 * This works differently than the built-in {cache: true} on an action, because
 * it caches the entity instead of the http call. This allows local changes
 * to the entity to be persistent across views.
 *
 * @example cachedResource('/endpoint/:id', {id: '@id'}, {query: {isArray: true}});
 *
 * Prevent caching an action by addding `cache: false`.
 * Uncache a result manually by calling entity.$uncache();
 */
export default function /* @ngInject */ cachedResource($cacheFactory, $resource) {
    let cache = $cacheFactory('cachedResourceCache');

    return function(url, paramDefaults, actions={}) {
        let resource, actionKeys;

        // Calls $resource with the same parameters.
        resource = $resource.apply($resource, arguments);
        actionKeys = Object.keys(resource);

        for (let i = 0, m = actionKeys.length; i < m; i++) {
            let key = actionKeys[i];
            if (allowCache(key)) {
                // Overwrite original $resource method with our proxy.
                resource[key] = cacheProxy(resource[key], actions[key]);
            }
        }

        // When an action is proxied, it adds a wrapper to the original action.
        // The wrapper calls the original function and caches the result before
        // returning it. A subsequent call with the same parameters will return
        // the cache and not hit the server.
        function cacheProxy(origFunction, action) {
            return function(param) {
                let cacheKey, result;

                cacheKey = JSON.stringify([url, action, param]);
                result = cache.get(cacheKey);

                if (!result) {
                    // No cache populated yet, call original resource function
                    // with the same parameters and set the cache.
                    result = origFunction.apply(origFunction, arguments);
                    cache.put(cacheKey, result);
                }

                // Add a public method to the entity to remove its cache.
                result.$uncache = function() {
                    return cache.remove(cacheKey);
                };

                return result;
            };
        }

        function allowCache(actionKey) {
            if (actions[actionKey]) {
                // Action explicitly forbids cache.
                if (actions[actionKey].cache === false) {
                    return false;
                }
                // Allow cache on overridden GET actions.
                return (
                    !actions[actionKey].method ||
                    actions[actionKey].method.toUpperCase() === 'GET'
                );
            }
            // Allow cache on default GET actions.
            return (actionKey === 'get' || actionKey === 'query');
        }

        return resource;
    };
}



// WEBPACK FOOTER //
// ./data/services/cached-resource.js